import { useState, useCallback, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { debounce } from "lodash"
import dayjs from "dayjs"
import { arrayMove } from "@dnd-kit/sortable"
import { getCrewProfiles } from "store/crew-profiles"
import { getRoles } from "store/roles"
import { transformCrewProfiles } from "../utils/transformCrewData"
import { api } from "store/helpers/api"
import { toaster } from "components/common/Toast"

/**
 * Custom hook for managing crew member filtering and pagination with backend API
 * @param {Function} t - Translation function
 * @returns {Object} Filter states, data, and handler functions
 */
export const useCrewFilters = (t) => {
  const dispatch = useDispatch()

  // Redux state
  const { items: crewData, metadata, loading } = useSelector((state) => state.crewProfiles)
  const { items: rolesData, loading: rolesLoading } = useSelector((state) => state.roles)

  // Local filter state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectedRoles, setSelectedRoles] = useState([])
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [sortBy, setSortBy] = useState("")
  const [sortDirection, setSortDirection] = useState("asc")
  // Manual row order: array of row keys for the current page.
  // Reset whenever the backend returns a new page of data.
  const [manualOrder, setManualOrder] = useState(null)

  // Fetch roles on mount
  useEffect(() => {
    dispatch(getRoles())
  }, [dispatch])

  // Debounced search handler (300ms delay)
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedSearch(value)
        setCurrentPage(1)
      }, 300),
    []
  )

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel()
    }
  }, [debouncedSetSearch])

  const fetchCrewProfiles = useCallback(() => {
    const roleIds = selectedRoles.length > 0 && !selectedRoles.includes("all") ? selectedRoles : []
    const status =
      selectedStatuses.length > 0 && !selectedStatuses.includes("all") ? selectedStatuses[0] : "all"
    const availableOn = selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : undefined
    dispatch(
      getCrewProfiles({
        search: debouncedSearch,
        status,
        roleIds,
        page: currentPage,
        per_page: pageSize,
        sort_by: sortBy || undefined,
        sort_direction: sortDirection,
        available_on: availableOn
      })
    )
  }, [dispatch, debouncedSearch, selectedRoles, selectedStatuses, selectedDate, currentPage, pageSize, sortBy, sortDirection])

  // Fetch crew profiles when filters change
  useEffect(() => {
    fetchCrewProfiles()
    // Reset manual order whenever we fetch a fresh page from the server
    setManualOrder(null)
  }, [fetchCrewProfiles])

  // Transform backend data to table format
  const transformedCrewData = useMemo(() => transformCrewProfiles(crewData), [crewData])

  // Apply manual drag order on top of the transformed data for the current page
  const orderedCrewData = useMemo(() => {
    if (!manualOrder) return transformedCrewData
    const keyOf = (row) => String(row.key ?? row.id)
    const map = new Map(transformedCrewData.map((row) => [keyOf(row), row]))
    const reordered = manualOrder.filter((key) => map.has(key)).map((key) => map.get(key))
    const inOrder = new Set(manualOrder)
    transformedCrewData.forEach((row) => {
      if (!inOrder.has(keyOf(row))) reordered.push(row)
    })
    return reordered
  }, [transformedCrewData, manualOrder])

  // Build role options from API data
  const roleOptions = useMemo(
    () => [
      { label: t("All") || "All", value: "all" },
      ...rolesData.map((role) => ({
        label: role.name,
        value: role.id
      }))
    ],
    [rolesData, t]
  )

  // Status filter options
  const statusOptions = useMemo(
    () => [
      { label: t("All") || "All", value: "all" },
      { label: t("Active") || "Active", value: "available" },
      { label: t("Deactivated") || "Deactivated", value: "disabled" }
    ],
    [t]
  )

  const expandedSelectedRoles = selectedRoles.includes("all")
    ? roleOptions.filter((r) => r.value !== "all").map((r) => r.value)
    : selectedRoles

  const expandedSelectedStatuses = selectedStatuses.includes("all")
    ? statusOptions.filter((s) => s.value !== "all").map((s) => s.value)
    : selectedStatuses

  const handleSearchChange = useCallback(
    (value) => {
      setSearch(value)
      debouncedSetSearch(value)
    },
    [debouncedSetSearch]
  )

  const handleRoleChange = useCallback((roles) => {
    setSelectedRoles(roles)
    setCurrentPage(1)
  }, [])

  const handleStatusChange = useCallback((statuses) => {
    setSelectedStatuses(statuses)
    setCurrentPage(1)
  }, [])

  const handleDateChange = useCallback((date) => {
    setSelectedDate(date || dayjs())
    setCurrentPage(1)
  }, [])

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page)
  }, [])

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size)
    setCurrentPage(1)
  }, [])

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    if (sorter?.columnKey) {
      const sortByMap = {
        name: "name",
        title: "title",
        role: "role",
        status: "status",
        assignedTo: "experiences",
        upcomingBookings: "upcoming",
        nextTimeOff: "next_time_off",
        availability: "availability"
      }

      const backendSortBy = sortByMap[sorter.columnKey]

      if (backendSortBy) {
        setSortBy(backendSortBy)
        setSortDirection(sorter.order === "descend" ? "desc" : "asc")
      }
    } else {
      setSortBy("")
      setSortDirection("asc")
    }
    // Clear manual order when column sorting changes
    setManualOrder(null)
  }, [])

  /**
   * Called when a row is dragged to a new position within the current page.
   * activeId and overId are string row keys.
   */
  const handleReorder = useCallback(
    (activeId, overId) => {
      const keyOf = (row) => String(row.key ?? row.id)
      const currentOrder = orderedCrewData.map(keyOf)
      const oldIndex = currentOrder.indexOf(activeId)
      const newIndex = currentOrder.indexOf(overId)
      if (oldIndex === -1 || newIndex === -1) return
      const newOrder = arrayMove(currentOrder, oldIndex, newIndex)
      setManualOrder(newOrder)
      const pageIds = newOrder.map(Number)
      dispatch(
        api({
          url: `/guide/profiles/reorder?page=${currentPage}&per_page=${pageSize}`,
          method: "put",
          data: { ids: pageIds }
        })
      ).then(() => {
        toaster.success("Crew order saved")
        fetchCrewProfiles()
      })
    },
    [orderedCrewData, currentPage, pageSize, dispatch, fetchCrewProfiles]
  )

  // Drag-to-reorder is mutually exclusive with column sorting: a manual order
  // can't be honored while the list is sorted by a column.
  const isSortActive = Boolean(sortBy)

  // Drag-to-reorder is also disabled while any filter narrows the list, since a
  // saved order only makes sense against the full, unfiltered list. The date
  // filter counts as active only when it's set to a day other than today.
  const isFilterActive = useMemo(() => {
    const hasSearch = Boolean(search?.trim())
    const hasRoles = selectedRoles.length > 0
    const hasStatuses = selectedStatuses.length > 0
    const hasDateFilter = Boolean(selectedDate) && !dayjs(selectedDate).isSame(dayjs(), "day")
    return hasSearch || hasRoles || hasStatuses || hasDateFilter
  }, [search, selectedRoles, selectedStatuses, selectedDate])

  // Reordering is disabled when either column sorting or any filter is active.
  const dragDisabled = isSortActive || isFilterActive

  return {
    // State
    search,
    selectedRoles,
    selectedStatuses,
    selectedDate,
    currentPage,
    pageSize,
    sortBy,
    sortDirection,
    isSortActive,
    dragDisabled,
    // Derived data
    roleOptions,
    statusOptions,
    expandedSelectedRoles,
    expandedSelectedStatuses,
    crewData: orderedCrewData,
    metadata,
    loading: loading || rolesLoading,
    // Handlers
    setSearch: handleSearchChange,
    setSelectedRoles: handleRoleChange,
    setSelectedStatuses: handleStatusChange,
    setSelectedDate: handleDateChange,
    setCurrentPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    handleTableChange,
    handleReorder
  }
}
